import { ApiError, BaseAPI } from "./BaseApi";

export type LobbyState =
  | "OPEN"
  | "PREPARING"
  | "ACTIVE"
  | "COMPLETED"
  | "CANCELLED";

export type LobbyVisibility = "PUBLIC" | "PRIVATE";

export interface LobbyMember {
  userId: string;
  username: string;
  avatarUrl?: string;
  joinedAt: string;
  isHost: boolean;
}

export interface Lobby {
  lobbyId: string;
  version: number;
  gameId: string;
  gameName?: string | null;
  gameCoverUrl?: string | null;
  title: string;
  description?: string;
  hostId: string;
  members: LobbyMember[];
  maxPlayers: number;
  state: LobbyState;
  visibility: LobbyVisibility;
  locked: boolean;
  preferences: {
    gameMode?: string;
    region?: string;
    skillLevel?: "casual" | "competitive";
    voiceRequired?: boolean;
  };
  discord?: {
    channelId?: string | null;
    guildId?: string | null;
    inviteLink?: string | null;
    channelCreationFailed?: boolean;
  };
  discordInviteLink?: string | null;
  matchId?: string | null;
  listingId?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  isFull: boolean;
  canJoin: boolean;
  canStart: boolean;
}

export interface LobbySettings {
  title?: string;
  description?: string;
  maxPlayers?: number;
  gameMode?: string;
  region?: string;
  skillLevel?: "casual" | "competitive";
  voiceRequired?: boolean;
}

export class LobbyAPI extends BaseAPI {
  constructor() {
    super("");
  }

  async joinLobby(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/join`, {
      method: "POST",
      auth: true,
    });
  }

  async leaveLobby(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/leave`, {
      method: "POST",
      auth: true,
    });
  }

  async kickPlayer(lobbyId: string, targetUserId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/kick`, {
      method: "POST",
      body: JSON.stringify({ targetUserId }),
      auth: true,
    });
  }

  async transferHost(lobbyId: string, targetUserId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/transfer-host`, {
      method: "POST",
      body: JSON.stringify({ targetUserId }),
      auth: true,
    });
  }

  async editSettings(lobbyId: string, settings: LobbySettings): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/settings`, {
      method: "PATCH",
      body: JSON.stringify(settings),
      auth: true,
    });
  }

  async lockLobby(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/lock`, {
      method: "POST",
      auth: true,
    });
  }

  async unlockLobby(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/unlock`, {
      method: "POST",
      auth: true,
    });
  }

  async cancelLobby(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/cancel`, {
      method: "POST",
      auth: true,
    });
  }

  async startMatch(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/start`, {
      method: "POST",
      auth: true,
    });
  }

  async confirmStart(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/confirm-start`, {
      method: "POST",
      auth: true,
    });
  }

  async completeMatch(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/complete`, {
      method: "POST",
      auth: true,
    });
  }

  async getLobby(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}`, {
      method: "GET",
      auth: true,
    });
  }

  async getMyLobby(): Promise<Lobby | null> {
    try {
      return await this.request<Lobby>("/api/lobbies/me", {
        method: "GET",
        auth: true,
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async retryDiscordChannel(lobbyId: string): Promise<Lobby> {
    return this.request<Lobby>(`/api/lobbies/${lobbyId}/retry-discord`, {
      method: "POST",
      auth: true,
    });
  }
}
